/**
 * ⚠️  VERIFY BEFORE PUBLISH  ⚠️
 * ---------------------------------------------------------------------------
 * Competitor facts below are the SINGLE SOURCE OF TRUTH for the /vs comparison
 * pages and the category landing page. They are seeded from two places:
 *   1. Figures given in MyEduLink's own marketing recommendation
 *      (Teachable ≈ $39/mo + 7.5% fee, Thinkific ≈ $54/mo, MyEduLink $19/mo).
 *   2. Best-known public pricing for the remaining cells (marked below).
 *
 * These are PUBLIC, comparative claims about named competitors. Confirm every
 * price / fee / feature against each competitor's live pricing page and update
 * `PRICING_AS_OF` before this ships. Language is deliberately hedged
 * ("no native live classes", "starts around") to stay accurate as plans change.
 * ---------------------------------------------------------------------------
 */

export const PRICING_AS_OF = "August 2026";

export const MY_STARTING_PRICE = "$19/mo";

export interface FaqItem {
    q: string;
    a: string;
}

export interface FeatureRow {
    /** What the row compares, e.g. "Starting price". */
    label: string;
    /** MyEduLink's value. */
    mine: string;
    /** The competitor's value. */
    theirs: string;
    /** True when MyEduLink is the stronger side (drives the check/‑ styling). */
    mineWins: boolean;
}

export interface CompetitorComparison {
    slug: string;
    /** Display name, e.g. "Teachable". */
    name: string;
    /** SEO <title>. */
    title: string;
    metaDescription: string;
    /** Page <h1>. */
    h1: string;
    /** One-paragraph intro under the h1. */
    intro: string;
    /** Short "from" price label for the competitor, e.g. "$39/mo". */
    theirStartingPrice: string;
    rows: FeatureRow[];
    /** Addresses the fear of switching / migration regret, inline. */
    migrationRegret: { heading: string; body: string };
    faqs: FaqItem[];
}

// Shared feature rows differ per competitor only in the `theirs` column, so we
// build them from a small per-competitor fact set to avoid repetition/drift.
interface CompetitorFacts {
    slug: string;
    name: string;
    startingPrice: string;      // seeded / verify
    transactionFee: string;     // seeded / verify
    liveClasses: string;        // seeded / verify
    marketplace: string;        // seeded / verify
    autoGrading: string;        // seeded / verify
    certificates: string;       // seeded / verify
    pricingNote: string;        // provenance note, shown in a small footnote
}

const COMPETITOR_FACTS: CompetitorFacts[] = [
    {
        slug: "teachable",
        name: "Teachable",
        startingPrice: "≈ $39/mo",
        transactionFee: "Up to 7.5% on lower tiers",
        liveClasses: "No native live classes (Zoom/third-party only)",
        marketplace: "No student marketplace",
        autoGrading: "Basic quizzes",
        certificates: "Certificates on higher tiers",
        pricingNote: "Starting price and 7.5% fee per MyEduLink's competitor brief; verify against teachable.com/pricing.",
    },
    {
        slug: "thinkific",
        name: "Thinkific",
        startingPrice: "≈ $54/mo",
        transactionFee: "0% platform fee",
        liveClasses: "Live only via Zoom integration (no native)",
        marketplace: "No student marketplace",
        autoGrading: "Quizzes & surveys",
        certificates: "Certificates included",
        pricingNote: "Starting price per MyEduLink's competitor brief; verify against thinkific.com/pricing.",
    },
    {
        slug: "kajabi",
        name: "Kajabi",
        startingPrice: "≈ $69/mo (Kickstarter)",
        transactionFee: "0% platform fee",
        liveClasses: "No native live classes (third-party only)",
        marketplace: "No student marketplace",
        autoGrading: "Assessments & quizzes",
        certificates: "Limited / add-on",
        pricingNote: "PUBLIC ESTIMATE — not in the competitor brief. Verify Kajabi's current entry price at kajabi.com/pricing before publishing.",
    },
];

const buildRows = (f: CompetitorFacts): FeatureRow[] => [
    { label: "Starting price", mine: MY_STARTING_PRICE, theirs: f.startingPrice, mineWins: true },
    { label: "Platform transaction fee", mine: "0% — you connect your own PayPal", theirs: f.transactionFee, mineWins: f.transactionFee.includes("7.5") },
    { label: "Native live classes", mine: "Yes — built in", theirs: f.liveClasses, mineWins: true },
    { label: "Auto-graded exams & homework", mine: "Yes — auto-grading built in", theirs: f.autoGrading, mineWins: true },
    { label: "Certificates", mine: "Yes — issue & revoke, built in", theirs: f.certificates, mineWins: true },
    { label: "Student marketplace", mine: "Yes — get discovered by students", theirs: f.marketplace, mineWins: true },
    { label: "Free trial", mine: "2 months free, no credit card", theirs: "Short free trial (varies)", mineWins: true },
];

const buildComparison = (f: CompetitorFacts): CompetitorComparison => ({
    slug: f.slug,
    name: f.name,
    title: `MyEduLink vs ${f.name} — a cheaper ${f.name} alternative with live classes, from $19/mo`,
    metaDescription: `Comparing MyEduLink and ${f.name} for tutors & academies: price, transaction fees, native live classes, auto-graded exams, certificates and a student marketplace. MyEduLink starts at $19/mo with a 2-month free trial.`,
    h1: `MyEduLink vs ${f.name}`,
    intro: `Looking for a ${f.name} alternative that runs live classes, grades exams automatically and gets you paid — without the higher monthly price? Here's how MyEduLink and ${f.name} compare for tutors and academies.`,
    theirStartingPrice: f.startingPrice,
    rows: buildRows(f),
    migrationRegret: {
        heading: `Thinking of switching from ${f.name}?`,
        body: `You won't lose your work. You can rebuild your courses on MyEduLink at your own pace while ${f.name} stays live, keep your existing students, and only cancel once you're ready. Your first 2 months are free with no credit card, so you can move a single course over, run a live class, and see the difference before committing.`,
    },
    faqs: [
        {
            q: `Is MyEduLink cheaper than ${f.name}?`,
            a: `MyEduLink starts at ${MY_STARTING_PRICE}, compared with ${f.startingPrice} for ${f.name} (as of ${PRICING_AS_OF}). MyEduLink also charges 0% platform transaction fee — you connect your own PayPal and get paid directly.`,
        },
        {
            q: `Does MyEduLink have live classes like ${f.name}?`,
            a: `Yes — live classes are built into MyEduLink, not bolted on through a third-party tool. You can run a live session, set an auto-graded exam and issue a certificate from the same dashboard.`,
        },
        {
            q: `Can I move my courses from ${f.name} to MyEduLink?`,
            a: `Yes. You can rebuild courses on MyEduLink while your ${f.name} account stays active, so there's no gap for your students. Your 2-month free trial gives you time to migrate before you pay anything.`,
        },
        {
            q: `Do I need technical skills to switch from ${f.name}?`,
            a: `No. MyEduLink is built for tutors and academies, not developers — create a course, run live classes, set exams and get paid without any setup or coding.`,
        },
    ],
});

export const COMPARISONS: CompetitorComparison[] = COMPETITOR_FACTS.map(buildComparison);

export const getComparison = (slug?: string): CompetitorComparison | undefined =>
    COMPARISONS.find(c => c.slug === slug?.toLowerCase());

/** Provenance footnotes, surfaced in small print on each page. */
export const COMPETITOR_PRICING_NOTES: Record<string, string> = Object.fromEntries(
    COMPETITOR_FACTS.map(f => [f.slug, f.pricingNote])
);

// ── Category landing page: "cheapest platform to teach live online" ──────────

export interface CategoryPageData {
    slug: string;
    title: string;
    metaDescription: string;
    h1: string;
    intro: string;
    /** Ordered cheapest-first for the price table. */
    priceTable: { name: string; price: string; nativeLive: string; isMine: boolean }[];
    faqs: FaqItem[];
}

export const CATEGORY_LIVE_TEACHING: CategoryPageData = {
    slug: "cheapest-platform-to-teach-live-online",
    title: "Cheapest platform to teach live online — MyEduLink from $19/mo",
    metaDescription:
        "The cheapest platform to teach live classes online for tutors & academies. Compare starting prices for MyEduLink, Teachable, Thinkific and Kajabi — MyEduLink starts at $19/mo with native live classes, auto-grading, certificates and 0% platform fee.",
    h1: "The cheapest platform to teach live online",
    intro:
        "If you want to teach live classes online without paying for tools you don't need, price matters. MyEduLink starts at $19/mo with native live classes, auto-graded exams, certificates and a 0% platform fee — a fraction of what the best-known course platforms charge to get started.",
    priceTable: [
        { name: "MyEduLink", price: MY_STARTING_PRICE, nativeLive: "Yes — built in", isMine: true },
        { name: "Teachable", price: "≈ $39/mo", nativeLive: "No native live classes", isMine: false },
        { name: "Thinkific", price: "≈ $54/mo", nativeLive: "Zoom integration only", isMine: false },
        { name: "Kajabi", price: "≈ $69/mo", nativeLive: "No native live classes", isMine: false },
    ],
    faqs: [
        {
            q: "What is the cheapest platform to teach live classes online?",
            a: `Among the widely used course platforms, MyEduLink has the lowest starting price at ${MY_STARTING_PRICE} and includes native live classes — so you don't pay extra for a separate live-teaching tool. Teachable, Thinkific and Kajabi start higher and rely on third-party tools for live sessions (prices as of ${PRICING_AS_OF}).`,
        },
        {
            q: "Does MyEduLink charge transaction fees on what I earn?",
            a: "No. MyEduLink charges a 0% platform transaction fee. You connect your own PayPal and get paid directly by your students.",
        },
        {
            q: "Can I try it before paying?",
            a: "Yes — MyEduLink gives you 2 months free with no credit card required. You can run live classes, set auto-graded exams and issue certificates during the trial.",
        },
        {
            q: "Is it suitable for a single tutor as well as an academy?",
            a: "Yes. MyEduLink has plans for individual tutors and for academies managing multiple tutors, courses and students.",
        },
    ],
};

/** Build a schema.org FAQPage object from FAQ items, for JSON-LD injection. */
export const buildFaqSchema = (faqs: FaqItem[]): Record<string, unknown> => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(f => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
});
